import { useRef, useCallback, useEffect } from 'react';
import { viewVideo } from '../api/videoService';

export const useViewTracking = (video, isPlaying, inView, publicationId) => {
  const viewStartTime = useRef(null);
  const lastTrackedTime = useRef(0);
  const hasTrackedStart = useRef(false);

  const safeTrackView = useCallback(async (viewData) => {
    if (!video?.id) return;
    setTimeout(async () => {
      try {
        await viewVideo(video.id, { ...viewData, publication_id: publicationId || '' });
      } catch { /* silent */ }
    }, 0);
  }, [video.id, publicationId]);
  
  // Track view start when video begins playing
  useEffect(() => {
    if (isPlaying && inView && !hasTrackedStart.current && video.id) {
      hasTrackedStart.current = true;
      viewStartTime.current = Date.now();
      lastTrackedTime.current = 0;
      
      // Track view start (non-blocking)
      safeTrackView({
        watchTimeSeconds: 0,
        watchPercentage: 0,
        videoDuration: video.duration || null,
        eventType: 'start'
      });
    }
  }, [isPlaying, inView, video.id, video.duration, safeTrackView]);
  
  // Track view completion when video goes out of view or stops playing
  useEffect(() => {
    return () => {
      // Cleanup function runs when component unmounts or dependencies change
      if (hasTrackedStart.current && viewStartTime.current) {
        const actualWatchTime = (Date.now() - viewStartTime.current) / 1000;
        const videoDuration = video.duration || 60; // fallback duration
        const watchPercentage = Math.min(100, (actualWatchTime / videoDuration) * 100);
        
        // Only track if user watched for at least 1 second
        if (actualWatchTime >= 1) {
          safeTrackView({
            watchTimeSeconds: actualWatchTime,
            watchPercentage: watchPercentage,
            videoDuration: videoDuration,
            eventType: 'exit'
          });
        }
      }
    };
  }, [video.id, video.duration, safeTrackView]);
  
  // Track when video goes out of view (user scrolled away)
  useEffect(() => {
    if (!inView && hasTrackedStart.current && viewStartTime.current) {
      const actualWatchTime = (Date.now() - viewStartTime.current) / 1000;
      const videoDuration = video.duration || 60;
      const watchPercentage = Math.min(100, (actualWatchTime / videoDuration) * 100);
      
      // Track the actual viewing session
      if (actualWatchTime >= 1) {
        safeTrackView({
          watchTimeSeconds: actualWatchTime,
          watchPercentage: watchPercentage,
          videoDuration: videoDuration,
          eventType: 'scroll_away'
        });
      }
    }
  }, [inView, video.id, video.duration, safeTrackView]);
  
  // Track video completion (when video actually ends)
  const trackVideoCompletion = useCallback((currentTime, duration) => {
    if (hasTrackedStart.current && viewStartTime.current && currentTime && duration) {
      const actualWatchTime = (Date.now() - viewStartTime.current) / 1000;
      const watchPercentage = Math.min(100, (currentTime / duration) * 100);
      
      // Only track completion if video was substantially watched
      if (watchPercentage >= 90) {
        safeTrackView({
          watchTimeSeconds: actualWatchTime,
          watchPercentage: watchPercentage,
          videoDuration: duration,
          eventType: 'completed'
        });
      }
      
      // Reset tracking state
      hasTrackedStart.current = false;
      viewStartTime.current = null;
      lastTrackedTime.current = 0;
    }
  }, [safeTrackView]);
  
  // Periodic tracking for long videos (every 20 seconds)
  useEffect(() => {
    if (isPlaying && inView && hasTrackedStart.current) {
      const interval = setInterval(() => {
        if (viewStartTime.current) {
          const actualWatchTime = (Date.now() - viewStartTime.current) / 1000;
          const videoDuration = video.duration || 60;
          const watchPercentage = Math.min(100, (actualWatchTime / videoDuration) * 100);
          
          // Send periodic updates every 20 seconds for better analytics granularity
          if (actualWatchTime > lastTrackedTime.current + 20) {
            lastTrackedTime.current = actualWatchTime;
            
            safeTrackView({
              watchTimeSeconds: actualWatchTime,
              watchPercentage: watchPercentage,
              videoDuration: videoDuration,
              eventType: 'progress'
            });
          }
        }
      }, 20000); // Every 20 seconds
      
      return () => clearInterval(interval);
    }
  }, [isPlaying, inView, video.duration, safeTrackView]);
  
  return {
    trackVideoCompletion
  };
};
